import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Coins, Bank, CurrencyBtc, ArrowRight, Clock, CheckCircle, XCircle, Wallet, TrendUp, Receipt } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EarningsPage = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [payoutMethod, setPayoutMethod] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [methodForm, setMethodForm] = useState({
    method: 'zelle', zelle_email: '', bank_account: '', bank_clabe: '', bank_name: '', crypto_wallet: '', crypto_currency: 'USDT',
  });

  const fetchData = useCallback(async () => {
    try {
      const [earningsRes, methodRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/payouts/earnings`),
        axios.get(`${API}/api/payouts/method`),
        axios.get(`${API}/api/payouts/history`),
      ]);
      setEarnings(earningsRes.data);
      setPayoutMethod(methodRes.data);
      setHistory(historyRes.data);
      if (methodRes.data.method) {
        setMethodForm(prev => ({ ...prev, ...methodRes.data }));
      }
    } catch (err) {
      console.error('Failed to fetch earnings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveMethod = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/payouts/method`, methodForm);
      toast.success('Payout method saved!');
      setShowSetup(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 10) {
      toast.error('Minimum withdrawal is $10.00');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/payouts/request`, { amount });
      toast.success(res.data.message);
      setShowWithdraw(false);
      setWithdrawAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24" data-testid="earnings-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">Earnings</h1>
        <p className="text-slate-400">Track your revenue and request payouts</p>
      </div>

      {/* Balance Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-500/20 via-green-600/10 to-emerald-700/5 border border-green-500/20 p-6 mb-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <p className="text-green-400/70 text-sm font-medium mb-1">Available Balance</p>
          <div className="flex items-center gap-3 mb-4">
            <Wallet weight="duotone" className="w-10 h-10 text-green-400" />
            <span className="font-heading text-5xl font-black text-white" data-testid="available-balance">${earnings?.available_balance?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowWithdraw(true)}
              disabled={!payoutMethod?.method || (earnings?.available_balance || 0) < 10}
              className="px-6 py-2.5 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="withdraw-btn"
            >
              <ArrowRight weight="bold" className="w-4 h-4" />
              Withdraw
            </button>
            <button
              onClick={() => setShowSetup(true)}
              className="px-6 py-2.5 border border-white/10 text-slate-300 rounded-full font-medium hover:text-white hover:border-white/20 transition-colors"
              data-testid="setup-payout-btn"
            >
              {payoutMethod?.method ? 'Update Payout Method' : 'Set Up Payout Method'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Earned', value: `$${earnings?.creator_usd?.toFixed(2) || '0.00'}`, icon: TrendUp, color: 'green' },
          { label: 'Revenue Share', value: `${earnings?.revenue_share_pct || 80}%`, icon: Receipt, color: 'rose' },
          { label: 'Withdrawn', value: `$${earnings?.total_withdrawn?.toFixed(2) || '0.00'}`, icon: ArrowRight, color: 'blue' },
          { label: 'Pending', value: `$${earnings?.total_pending?.toFixed(2) || '0.00'}`, icon: Clock, color: 'amber' },
        ].map(s => (
          <div key={s.label} className="bg-slate-900/50 border border-white/5 rounded-2xl p-4">
            <s.icon className={`w-5 h-5 mb-2 text-${s.color}-400`} />
            <p className="text-xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Deal Info */}
      {earnings?.deal_title && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8 flex items-center justify-between">
          <div>
            <p className="text-amber-400 font-semibold text-sm">Active Deal: {earnings.deal_title}</p>
            <p className="text-slate-500 text-xs">You keep {earnings.revenue_share_pct}% of all earnings</p>
          </div>
          <span className="text-2xl font-black text-amber-400">{earnings.revenue_share_pct}%</span>
        </div>
      )}

      {/* Earnings Breakdown */}
      <div className="mb-8">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Earnings Breakdown (Tokens)</h2>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Tips', value: earnings?.breakdown?.tips || 0 },
            { label: 'Subscriptions', value: earnings?.breakdown?.subscriptions || 0 },
            { label: 'Content Unlocks', value: earnings?.breakdown?.content_unlocks || 0 },
          ].map(b => (
            <div key={b.label} className="bg-slate-900/50 border border-white/5 rounded-xl p-4 text-center">
              <Coins className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{b.value}</p>
              <p className="text-xs text-slate-500">{b.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Payout History */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Payout History</h2>
        {history.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/50 border border-white/5 rounded-2xl">
            <Wallet className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No payouts yet</p>
            <p className="text-slate-600 text-sm">Request a withdrawal when you reach $10</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(p => (
              <div key={p.request_id} className="flex items-center gap-4 p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  p.status === 'approved' ? 'bg-green-500/20' : p.status === 'rejected' ? 'bg-red-500/20' : 'bg-amber-500/20'
                }`}>
                  {p.status === 'approved' ? <CheckCircle className="w-5 h-5 text-green-400" /> :
                   p.status === 'rejected' ? <XCircle className="w-5 h-5 text-red-400" /> :
                   <Clock className="w-5 h-5 text-amber-400" />}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">${p.amount.toFixed(2)} via {p.method || 'N/A'}</p>
                  <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  p.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  p.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>{p.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payout Method Setup Modal */}
      {showSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowSetup(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto overscroll-contain" onClick={e => e.stopPropagation()} data-testid="payout-method-modal">
            <h3 className="text-xl font-bold text-white mb-4">Payout Method</h3>
            <div className="flex gap-2 mb-4">
              {[{id:'zelle',label:'Zelle',icon:Bank},{id:'bank_transfer',label:'Bank',icon:Bank},{id:'crypto',label:'Crypto',icon:CurrencyBtc}].map(m => (
                <button key={m.id} onClick={() => setMethodForm({...methodForm, method: m.id})}
                  className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    methodForm.method === m.id ? 'border-green-500/30 bg-green-500/10 text-white' : 'border-white/5 text-slate-500 hover:text-white'
                  }`}>
                  <m.icon className="w-4 h-4" />{m.label}
                </button>
              ))}
            </div>
            {methodForm.method === 'zelle' && (
              <div className="mb-4">
                <label className="text-sm text-slate-400 mb-1 block">Zelle Email</label>
                <input type="email" value={methodForm.zelle_email} onChange={e => setMethodForm({...methodForm, zelle_email: e.target.value})}
                  placeholder="your@email.com" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" data-testid="payout-zelle-email" />
              </div>
            )}
            {methodForm.method === 'bank_transfer' && (
              <div className="space-y-3 mb-4">
                <div><label className="text-sm text-slate-400 mb-1 block">Bank Name</label>
                  <input type="text" value={methodForm.bank_name} onChange={e => setMethodForm({...methodForm, bank_name: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" /></div>
                <div><label className="text-sm text-slate-400 mb-1 block">Account Number</label>
                  <input type="text" value={methodForm.bank_account} onChange={e => setMethodForm({...methodForm, bank_account: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" /></div>
                <div><label className="text-sm text-slate-400 mb-1 block">CLABE (Mexico)</label>
                  <input type="text" value={methodForm.bank_clabe} onChange={e => setMethodForm({...methodForm, bank_clabe: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" /></div>
              </div>
            )}
            {methodForm.method === 'crypto' && (
              <div className="space-y-3 mb-4">
                <div><label className="text-sm text-slate-400 mb-1 block">Currency</label>
                  <select value={methodForm.crypto_currency} onChange={e => setMethodForm({...methodForm, crypto_currency: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                    <option>USDT</option><option>BTC</option><option>ETH</option><option>USDC</option>
                  </select></div>
                <div><label className="text-sm text-slate-400 mb-1 block">Wallet Address</label>
                  <input type="text" value={methodForm.crypto_wallet} onChange={e => setMethodForm({...methodForm, crypto_wallet: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 font-mono text-sm" /></div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setShowSetup(false)} className="flex-1 py-3 border border-white/10 text-slate-400 rounded-full hover:text-white transition-colors">Cancel</button>
              <button onClick={handleSaveMethod} disabled={submitting}
                className="flex-1 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
                data-testid="save-payout-method-btn">
                {submitting ? 'Saving...' : 'Save Method'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowWithdraw(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto overscroll-contain" onClick={e => e.stopPropagation()} data-testid="withdraw-modal">
            <h3 className="text-xl font-bold text-white mb-2">Request Payout</h3>
            <p className="text-slate-500 text-sm mb-4">Available: <span className="text-green-400 font-semibold">${earnings?.available_balance?.toFixed(2)}</span></p>
            <div className="mb-4">
              <label className="text-sm text-slate-400 mb-1 block">Amount (USD)</label>
              <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                placeholder="Min $10.00" min={10} step={0.01}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white text-xl font-bold" data-testid="withdraw-amount-input" />
            </div>
            <p className="text-slate-600 text-xs mb-4">Paid via {payoutMethod?.method === 'zelle' ? 'Zelle' : payoutMethod?.method === 'bank_transfer' ? 'Bank Transfer' : 'Crypto'} within 48 hours</p>
            <div className="flex gap-3">
              <button onClick={() => setShowWithdraw(false)} className="flex-1 py-3 border border-white/10 text-slate-400 rounded-full hover:text-white transition-colors">Cancel</button>
              <button onClick={handleWithdraw} disabled={submitting}
                className="flex-1 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition-colors disabled:opacity-50"
                data-testid="confirm-withdraw-btn">
                {submitting ? 'Requesting...' : 'Request Payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EarningsPage;
